import React from 'react';
import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

/**
 * [Web UX 1. csomag, 2026-10-03] „Jelentkeztél" jelző a naptár-nézetekben (heti kártya,
 * havi chip, napi idővonal) — EGY helyen, hogy a három nézet ne térjen el egymástól.
 *
 * Miért: a weben a naptárból nem látszott, hova jelentkezett a játékos (a jelentés 4.2
 * pontja); minden eseményt meg kellett nyitni. A mobil ugyanezt a szót használja: a Főoldal és az
 * Edzések lista jelvénye (`mobile/components/EventStatusBadge.tsx:58,96`) és az esemény-részletező
 * zöld sora (`mobile/app/events/[id].tsx:546-558`, szövege `mobile/utils/eventDetailStatus.ts`
 * `registeredBannerText`; toast: `:237`). [Javítva 2026-10-04: a korábbi
 * fejkomment az `events.tsx`-re hivatkozott — abban ma nincs ilyen szöveg, a közös komponens adja.]
 * A szó a két platformon egyezik; a gombfeliratok MA: web „Jelentkezés / Leiratkozás"
 * (`EventDetailsDialog.tsx:180-182,771,782`), mobil „Jelentkezés / Lejelentkezés" (`events/[id].tsx:265-267,
 * 584,603` — a mobil „Feliratkozás"-t 2026-10-04-én, a szóhasználat-egységesítésnél felcserélte
 * „Jelentkezés"-re). Egyedül a „Leiratkozás" (web) és a „Lejelentkezés" (mobil) tér el még.
 * [Sorszámok frissítve 2026-10-15, dokumentáció-audit.]
 *
 * Forrás: a szerver `IsRegistered` mezője — `CalendarController` heti, napi ÉS havi
 * végpontja, mindhárom `UserEvents.Any(ue => ue.UserId == hívó && ue.UnregisteredAt == null)`.
 * Ugyanez a fogalom, mint az eseményablak állapotsora (`EventDetailsDialog`, résztvevő-lista).
 *
 * Sorrend (a mobil mintája): függő (isPending) > Jelentkeztél > kapacitás. Ezt a
 * `showRegisteredMarker` segéd adja, hogy a nézetek ne egyenként döntsenek.
 *
 * Nem csak szín: pipa-ikon + szöveg (vagy akadálymentes név a havi chipen).
 */

export const REGISTERED_LABEL = 'Jelentkeztél';

/** Mutassuk-e a „Jelentkeztél" jelzőt: csak aktív részvételnél, és a függő állapot előzi. */
export function showRegisteredMarker(event: { isRegistered?: boolean; isPending?: boolean }): boolean {
  return event.isRegistered === true && event.isPending !== true;
}

/** A havi chip akadálymentes neve: a látható felirat elé kerül a „Jelentkeztél". */
export function registeredAriaLabel(visibleLabel: string): string {
  return `${REGISTERED_LABEL}: ${visibleLabel}`;
}

/** Kis pipa-ikon a havi chip elejére (a chip `icon` propjához). A nevet a chip aria-label-je adja. */
export const RegisteredIcon: React.FC = () => (
  <CheckCircleIcon data-testid="registered-icon" aria-hidden="true" />
);

interface RegisteredMarkerProps {
  /** `tiny`: a heti kártya 14 px-es címkéje; `small`: a napi idővonal. */
  size?: 'tiny' | 'small';
  sx?: SxProps<Theme>;
}

/**
 * Ikon + „Jelentkeztél" szöveg. Fehér alapon sötétzöld: a heti kártya háttere az
 * esemény-típus tetszőleges színe, ezen a fehér címke minden színnél olvasható.
 */
const RegisteredMarker: React.FC<RegisteredMarkerProps> = ({ size = 'small', sx }) => {
  const tiny = size === 'tiny';
  return (
    <Chip
      data-testid="registered-marker"
      icon={<CheckCircleIcon aria-hidden="true" />}
      label={REGISTERED_LABEL}
      size="small"
      sx={[
        {
          height: tiny ? '14px' : 20,
          fontSize: tiny ? '0.55rem' : '0.7rem',
          fontWeight: 700,
          backgroundColor: '#fff',
          color: 'success.dark',
          border: '1px solid',
          borderColor: 'success.main',
          '& .MuiChip-icon': {
            color: 'success.main',
            fontSize: tiny ? '0.7rem' : '0.9rem',
            ml: tiny ? '2px' : '4px',
            mr: tiny ? '-4px' : '-2px',
          },
          '& .MuiChip-label': { px: tiny ? '4px' : '6px' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

export default RegisteredMarker;
